import { useActivateAdmin, useRecoverAdminPin } from "@/raids/rest/RaidQueries.ts";
import { getPinForRaid, storePinForRaid } from "@/raids/pinStore.ts";
import { Button, Input, Modal, Typography, notification } from "antd";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

type Props = {
  raidId: number;
  open: boolean;
  onClose: () => void;
};

export function PinEntryModal({ raidId, open, onClose }: Props) {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [showForgotHint, setShowForgotHint] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPin(getPinForRaid(raidId) ?? "");
    setShowForgotHint(false);
  }, [open, raidId]);

  const activate = useActivateAdmin({
    onSuccess: (access) => {
      storePinForRaid(raidId, access.adminPin);
      onClose();
      void navigate({
        to: "/raids/$raidId/edit",
        params: { raidId: String(raidId) },
      });
    },
    onError: (status, data) => {
      if (status === 403) {
        void Modal.error({
          title: "PIN ungültig",
          content: "Die eingegebene PIN ist falsch.",
        });
        return;
      }
      const msg = typeof data === "string" ? data : "";
      void Modal.error({
        title: "Authorisierung fehlgeschlagen",
        content: msg || `Status ${String(status ?? "?")}`,
      });
    },
  });

  const recover = useRecoverAdminPin({
    onSuccess: (access) => {
      storePinForRaid(raidId, access.adminPin);
      notification.warning({
        message: "Admin-PIN erneuert",
        description: (
          <Typography.Text copyable={{ text: access.adminPin }}>
            PIN: {access.adminPin}
          </Typography.Text>
        ),
        duration: 5,
      });
      setPin(access.adminPin);
      setShowForgotHint(false);
    },
    onError: (status, data) => {
      const axMsg = typeof data === "string" ? data : undefined;
      if (status === 403) {
        void Modal.error({
          title: "PIN nicht abrufbar",
          content:
            "Nur die Ersteller-Sitzung darf die PIN per „vergessen“ laden. Bitte die ursprüngliche PIN verwenden.",
        });
      } else {
        void Modal.error({
          title: "Fehler",
          content: axMsg ?? `Status ${String(status)}`,
        });
      }
    },
  });

  function submit() {
    const trimmed = pin.trim();
    if (!trimmed) return;
    activate.mutate({ raidId, pin: trimmed });
  }

  return (
    <Modal
      title="Admin-PIN"
      open={open}
      onCancel={onClose}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={onClose}>
          Abbrechen
        </Button>,
        <Button
          key="ok"
          type="primary"
          loading={activate.isPending}
          onClick={() => {
            submit();
          }}
        >
          Bearbeiten
        </Button>,
      ]}
    >
      <div className="flex flex-col gap-3">
        <Typography.Text type="secondary">
          Bitte die sechsstellige Admin-PIN eingeben, um den Raid zu bearbeiten.
        </Typography.Text>
        <Input.Password
          value={pin}
          onChange={(e) => {
            setPin(e.target.value);
          }}
          onPressEnter={() => {
            submit();
          }}
          placeholder="PIN"
          maxLength={32}
          autoComplete="one-time-code"
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="link"
            className="p-0!"
            onClick={() => {
              setShowForgotHint((v) => !v);
            }}
          >
            PIN vergessen?
          </Button>
          {showForgotHint && (
            <Button
              size="small"
              loading={recover.isPending}
              onClick={() => {
                recover.mutate(raidId);
              }}
            >
              PIN für diese Sitzung anzeigen
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
